import React, { useEffect, useState } from "react";
import { Loader2, Library as LibraryIcon, Heart, X, Quote } from "lucide-react";
import { toast } from "sonner";
import api, { monthName, prettyDate } from "@/lib/api";

const EntryModal = ({ entry, onClose }) => {
  if (!entry) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-nightsea/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-card border border-border rounded-2xl shadow-xl animate-fade-up p-6 sm:p-8">
        <button onClick={onClose} data-testid="entry-modal-close" className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        <p className="text-xs uppercase tracking-widest text-brass font-semibold">{prettyDate(entry.date)} · {entry.month_theme}</p>
        <h2 className="font-heading text-2xl font-semibold mt-2">{entry.title}</h2>
        {entry.quote && (
          <div className="mt-4 flex gap-2 text-foreground/80"><Quote className="w-5 h-5 text-brass shrink-0" /><p className="font-heading italic">{entry.quote}</p></div>
        )}
        <p className="mt-5 text-foreground/90 leading-relaxed whitespace-pre-wrap">{entry.reflection}</p>
        {entry.affirmation && <div className="mt-5 bg-primary text-primary-foreground rounded-xl p-4 font-heading italic">{entry.affirmation}</div>}
      </div>
    </div>
  );
};

const Library = () => {
  const now = new Date();
  const [tab, setTab] = useState("calendar");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadMonth = async (m) => {
    setLoading(true);
    try { const { data } = await api.get(`/entries/month/${m}`); setEntries(Array.isArray(data) ? data : data.entries || []); }
    catch (e) { toast.error("Could not load entries"); setEntries([]); }
    finally { setLoading(false); }
  };

  const loadFavorites = async () => {
    setLoading(true);
    try { const { data } = await api.get("/favorites"); setFavorites(Array.isArray(data) ? data : data.favorites || []); }
    catch (e) { setFavorites([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (tab === "calendar") loadMonth(month); }, [month, tab]);
  useEffect(() => { if (tab === "favorites") loadFavorites(); }, [tab]);

  const openEntry = async (date) => {
    try { const { data } = await api.get(`/entries/date/${date}`); setSelected(data); }
    catch (e) { toast.error("Could not open entry"); }
  };

  const list = tab === "calendar" ? entries : favorites;

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-2 text-primary"><LibraryIcon className="w-5 h-5" /></div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold">Library</h1>
        <p className="text-muted-foreground mt-2 mb-6">Browse every daily reading and revisit your favorites.</p>

        <div className="flex gap-2 mb-6">
          <button data-testid="library-tab-calendar" onClick={() => setTab("calendar")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === "calendar" ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>Calendar</button>
          <button data-testid="library-tab-favorites" onClick={() => setTab("favorites")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === "favorites" ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>Favorites</button>
        </div>

        {tab === "calendar" && (
          <div className="flex flex-wrap gap-1.5 mb-6">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <button key={m} data-testid={`month-${m}`} onClick={() => setMonth(m)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${month === m ? "bg-secondary text-foreground font-semibold" : "text-muted-foreground hover:bg-secondary/60"}`}>
                {monthName(m).slice(0, 3)}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : list.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {tab === "favorites" ? <Heart className="w-8 h-8 mx-auto mb-3 opacity-40" /> : null}
            <p>{tab === "favorites" ? "No favorites yet. Tap the heart on any reading." : "No entries for this month."}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="library-grid">
            {list.map((e, i) => (
              <button key={e.date || i} data-testid="library-entry" onClick={() => openEntry(e.date)}
                className="text-left bg-card border border-border rounded-2xl p-5 hover:border-primary transition-colors">
                <p className="text-xs text-brass font-semibold mb-1">{prettyDate(e.date)}</p>
                <p className="font-heading text-lg font-medium leading-snug">{e.title}</p>
                {e.month_theme && <p className="text-xs text-muted-foreground mt-2">{e.month_theme}</p>}
              </button>
            ))}
          </div>
        )}
      </div>
      <EntryModal entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default Library;
